import type {
  BenefitSelectionChild,
  CoverageDefinition,
  CoverageSchemaResponse,
} from '@/features/quotes/types/group-life/coverage-schema.types';
import { benefitSumInsured } from '@/features/quotes/utils/group-life/benefit-sum-insured.util';
import { isDailyBenefitCoverage } from '@/features/quotes/utils/group-life/daily-benefit';
import { formatCurrencyBRL } from '@/features/quotes/utils/group-life/format';

/** Estado por cobertura, na união discriminada do contexto. */
export type CoverageSelection =
  | { kind: 'DECLARED'; enabled: boolean; capital: number | null }
  | {
      kind: 'PROPORTIONAL_TO_COVERAGE';
      enabled: boolean;
      proportionOverride?: number;
    }
  | {
      kind: 'PROPORTIONAL_TO_RISK';
      enabled: boolean;
      proportionOverride?: number;
    }
  | {
      kind: 'BENEFIT_SELECTION';
      enabled: boolean;
      selectedChildCoverageCode: string | null;
    }
  | {
      kind: 'BENEFIT_SELECTION_WITH_SI';
      enabled: boolean;
      selectedChildCoverageCode: string | null;
      capital: number | null;
    }
  | { kind: 'EXCLUSIVE_GROUP'; enabled: boolean };

export type ResolvedCoverage = {
  code: string;
  enabled: boolean;
  /** Capital final (em reais). `null` quando a cobertura não tem capital (EXCLUSIVE_GROUP) ou está desabilitada. */
  resolvedCapital: number | null;
  errors: string[];
};

export type ResolverContext = {
  /**
   * Soma dos capitais de todas as vidas cadastradas (estipulante +
   * subestipulantes). Usado como default para `RiskBasisCode = MemberCapital`.
   * TODO: confirmar com produto qual a agregação correta (soma, máximo,
   * média ou per-member). Default atual: SOMA.
   */
  totalMemberCapital: number;
  /**
   * Outros valores potencialmente referenciáveis por `RiskBasisCode`.
   * Quando o código não é reconhecido, o resolver emite erro.
   */
  riskBases?: Record<string, number>;
  /**
   * Teto da diária (R$/dia) já consultado na tabela DailyBenefitRate, por
   * código da cobertura e do benefit atualmente selecionado.
   */
  dailyBenefitMaxByCoverage?: Record<string, number>;
};

const MAX_CHAIN_DEPTH = 10;

/** Tolerância (meio centavo) da comparação de capital com `sumInsuredMax`. */
const HALF_CENT = 0.005;

/**
 * Modos em que o `sumInsuredMax` bloqueia: os que o corretor DIGITA o capital.
 * Nos proporcionais (só percentual) o teto LIMITA o capital (ver `CAPITAL_CAPPED`).
 */
const CAPITAL_CAP_ENFORCED: ReadonlySet<
  CoverageDefinition['sumInsuredDefinition']
> = new Set(['DECLARED', 'BENEFIT_SELECTION_WITH_SI']);

/**
 * Modos em que o capital é DERIVADO do percentual e o `sumInsuredMax` é o
 * limite aplicado pelo motor: `min(percentual × base, teto)` — mesma regra de
 * `resolveGroupLifeIndividualLimits` na Api. Sem isso a tela mostrava
 * "Rescisão 10% de 800 mil = R$ 80.000" enquanto o cálculo usava R$ 15.000.
 * Tetos só de exibição (`sumInsuredMaxDisplay`) NÃO entram aqui.
 */
const CAPITAL_CAPPED: ReadonlySet<CoverageDefinition['sumInsuredDefinition']> =
  new Set(['PROPORTIONAL_TO_COVERAGE', 'PROPORTIONAL_TO_RISK']);

/**
 * Resolve o capital final de cada cobertura selecionada, propagando cadeias
 * de PROPORTIONAL_TO_COVERAGE e calculando PROPORTIONAL_TO_RISK a partir do
 * contexto. Detecta ciclos e referência a coberturas desabilitadas.
 */
export function resolveAll(
  schema: CoverageSchemaResponse,
  selections: Record<string, CoverageSelection | undefined>,
  context: ResolverContext
): Record<string, ResolvedCoverage> {
  const out: Record<string, ResolvedCoverage> = {};
  const byCode = new Map<string, CoverageDefinition>();
  for (const cov of schema.coverages) byCode.set(cov.code, cov);

  // Membros ATIVOS do grupo de exclusão, computados uma vez: cada cobertura
  // habilitada com `exclusionGroupCode` E o benefício escolhido de cada
  // cobertura de seleção habilitada. O grupo atravessa TIPOS — um benefício
  // pode ser exclusivo com uma cobertura — por isso benefícios entram no mesmo
  // conjunto. `ownerCode` é a cobertura em cuja LINHA o erro aparece (a do
  // próprio item, ou a cobertura-pai do benefício).
  const activeExclusionMembers = collectActiveExclusionMembers(
    schema.coverages,
    selections
  );

  // Acoplamento de NÍVEL (`LinkedBenefitCode`), computado uma vez. Só dispara
  // com as duas coberturas contratadas; o erro aparece nas DUAS linhas (a da
  // cobertura que carrega o link e a da pareada), por isso é um mapa
  // code → mensagens, lido dentro do resolve de cada cobertura.
  const benefitLinkConflicts = collectBenefitLinkConflicts(
    schema.coverages,
    selections,
    byCode
  );

  const resolve = (code: string, visiting: Set<string>): ResolvedCoverage => {
    if (out[code]) return out[code];

    const cov = byCode.get(code);
    if (!cov) {
      const r: ResolvedCoverage = {
        code,
        enabled: false,
        resolvedCapital: null,
        errors: [`Cobertura ${code} não encontrada no schema`],
      };
      out[code] = r;
      return r;
    }

    const sel = selections[code];
    const enabled = !!sel?.enabled;
    const errors: string[] = [];

    if (!enabled) {
      const r: ResolvedCoverage = {
        code,
        enabled: false,
        resolvedCapital: null,
        errors: [],
      };
      out[code] = r;
      return r;
    }

    if (visiting.has(code) || visiting.size > MAX_CHAIN_DEPTH) {
      const r: ResolvedCoverage = {
        code,
        enabled,
        resolvedCapital: null,
        errors: [`Ciclo detectado envolvendo ${code}`],
      };
      out[code] = r;
      return r;
    }

    let resolvedCapital: number | null = null;

    switch (cov.sumInsuredDefinition) {
      case 'DECLARED': {
        if (sel?.kind !== 'DECLARED') {
          errors.push(`Estado inválido para ${code} (esperado DECLARED)`);
          break;
        }
        const cap = sel.capital ?? 0;
        if (cov.sumInsuredMin != null && cap < cov.sumInsuredMin) {
          errors.push(
            `${cov.name}: capital mínimo R$ ${cov.sumInsuredMin.toLocaleString('pt-BR')}`
          );
        }
        resolvedCapital = cap;
        break;
      }
      case 'PROPORTIONAL_TO_COVERAGE': {
        const ref = byCode.get(cov.referenceCoverageCode);
        if (!ref) {
          errors.push(
            `${cov.name} referencia cobertura ${cov.referenceCoverageCode} inexistente`
          );
          break;
        }
        const refSel = selections[ref.code];
        if (!refSel?.enabled) {
          errors.push(
            `${cov.name} depende de ${ref.name}, que está desabilitada`
          );
          break;
        }
        const nextVisiting = new Set(visiting);
        nextVisiting.add(code);
        const refResolved = resolve(ref.code, nextVisiting);
        if (refResolved.resolvedCapital == null) {
          errors.push(
            `${cov.name} depende de ${ref.name}, que não tem capital resolvido`
          );
          break;
        }
        const pct =
          sel?.kind === 'PROPORTIONAL_TO_COVERAGE' &&
          sel.proportionOverride != null
            ? sel.proportionOverride
            : cov.proportion;
        // Só percentual NEGATIVO é inválido. Percentuais > 100% são legítimos
        // no produto — ex.: "Invalidez Permanente Total ou Parcial por Acidente"
        // (8200003) é 200% de "Morte Acidental" no schema do InsureMO.
        if (pct < 0) {
          errors.push(`${cov.name}: percentual não pode ser negativo`);
        }
        resolvedCapital = refResolved.resolvedCapital * (pct / 100);
        break;
      }
      case 'PROPORTIONAL_TO_RISK': {
        // Código de base de risco desconhecido cai no capital total das
        // vidas (default seguro) em vez de bloquear o Step 2 com erro —
        // o catálogo real de RiskBasisCode do produto ainda não foi
        // mapeado (pendência de produto). Apenas avisa no console.
        const basis = readRiskBasis(cov.riskBasisCode, context);
        const pct =
          sel?.kind === 'PROPORTIONAL_TO_RISK' && sel.proportionOverride != null
            ? sel.proportionOverride
            : cov.proportion;
        // Só percentual NEGATIVO é inválido — percentuais acima de 100% são
        // legítimos (ver PROPORTIONAL_TO_COVERAGE acima).
        if (pct < 0) {
          errors.push(`${cov.name}: percentual não pode ser negativo`);
        }
        resolvedCapital = basis * (pct / 100);
        break;
      }
      case 'BENEFIT_SELECTION': {
        if (sel?.kind !== 'BENEFIT_SELECTION') {
          errors.push(
            `Estado inválido para ${code} (esperado BENEFIT_SELECTION)`
          );
          break;
        }
        const childCode = sel.selectedChildCoverageCode;
        if (!childCode) {
          errors.push(`${cov.name}: selecione uma opção`);
          break;
        }
        const child = cov.children.find(c => c.code === childCode);
        if (!child) {
          errors.push(`${cov.name}: opção ${childCode} inválida`);
          break;
        }
        // A IS do benefício está em `DefaultSI` no 930016 — ver
        // `benefitSumInsured`. Lendo só `sumInsured`, todo auxílio resolvia 0.
        resolvedCapital = benefitSumInsured(child) ?? 0;
        break;
      }
      case 'BENEFIT_SELECTION_WITH_SI': {
        if (sel?.kind !== 'BENEFIT_SELECTION_WITH_SI') {
          errors.push(
            `Estado inválido para ${code} (esperado BENEFIT_SELECTION_WITH_SI)`
          );
          break;
        }
        if (!sel.selectedChildCoverageCode) {
          errors.push(`${cov.name}: selecione uma opção`);
        }
        // O valor da cobertura é OBRIGATÓRIO neste tipo — o corretor digita o
        // capital com base na opção escolhida. Sem valor (nulo ou ≤ 0) →
        // bloqueia o avanço para o Step 3.
        const cap = sel.capital;
        if (cap == null || cap <= 0) {
          errors.push(`${cov.name}: informe o valor da cobertura`);
        } else {
          const dailyMax = isDailyBenefitCoverage(code)
            ? context.dailyBenefitMaxByCoverage?.[code]
            : undefined;
          if (dailyMax != null && cap - dailyMax > HALF_CENT) {
            errors.push(`Máximo permitido: ${formatCurrencyBRL(dailyMax)}`);
          } else if (cov.sumInsuredMin != null && cap < cov.sumInsuredMin) {
            errors.push(
              `${cov.name}: capital mínimo R$ ${cov.sumInsuredMin.toLocaleString('pt-BR')}`
            );
          }
        }
        resolvedCapital = cap ?? 0;
        break;
      }
      case 'EXCLUSIVE_GROUP':
        // Não define IS; apenas exclusividade no grupo. resolvedCapital fica null.
        resolvedCapital = null;
        break;
    }

    if (
      CAPITAL_CAPPED.has(cov.sumInsuredDefinition) &&
      resolvedCapital != null &&
      cov.sumInsuredMax != null &&
      cov.sumInsuredMax > 0 &&
      resolvedCapital - cov.sumInsuredMax > HALF_CENT
    ) {
      resolvedCapital = cov.sumInsuredMax;
    }

    /*
     * Teto de capital (`sumInsuredMax`) — só TRAVA nos modos em que o corretor
     * digita o capital. Nas proporcionais (input só de percentual) o capital
     * vem da cobertura de referência / base de risco e é LIMITADO ao teto
     * acima, sem bloquear o Step 2.
     *
     * A comparação tem tolerância de meio centavo: um teto com centavos (ex.:
     * 250.000,50) não pode empatar com um capital tipo 250000.50000000003 e
     * travar o Step 2 sem motivo.
     */
    if (
      CAPITAL_CAP_ENFORCED.has(cov.sumInsuredDefinition) &&
      resolvedCapital != null &&
      cov.sumInsuredMax != null &&
      resolvedCapital - cov.sumInsuredMax > HALF_CENT
    ) {
      errors.push(
        `${cov.name}: capital de ${formatCurrencyBRL(resolvedCapital)} excede o máximo de ${formatCurrencyBRL(cov.sumInsuredMax)}.`
      );
    }

    /*
     * Exclusividade vale para QUALQUER modo, não só `EXCLUSIVE_GROUP`: os
     * Auxílios Funeral são `BENEFIT_SELECTION` (select de valor) e pertencem ao
     * grupo `AUXILIO_FUNERAL`. Checar dentro do case fazia a regra não valer
     * justamente para eles.
     *
     * O conflito é ACUSADO, não corrigido — nada é desmarcado automaticamente,
     * o corretor decide qual manter (mesmo padrão do Capital Global). Como
     * `resolveAll` roda a cada mudança de seleção, a mensagem é em tempo real.
     */
    /*
     * Dependência (`RelationType=1`): só pode contratar com a pareada ligada.
     * No 930016 é o que amarra cada Assistência Funeral ao Auxílio Funeral
     * equivalente — só entra a assistência do auxílio que foi escolhido.
     *
     * Parent inexistente vira erro explícito de propósito: o produto já teve
     * `ParentCoverageCode` apontando para código sem o zero à esquerda, e
     * falhar em silêncio esconderia esse tipo de erro de configuração.
     */
    if (cov.relationType === '1' && cov.parentCoverageCode) {
      const required = byCode.get(cov.parentCoverageCode);
      if (!required) {
        errors.push(
          `${cov.name} depende da cobertura ${cov.parentCoverageCode}, que não existe no produto`
        );
      } else if (!selections[cov.parentCoverageCode]?.enabled) {
        errors.push(
          `A cobertura ${cov.name} só pode ser contratada junto com ${required.name}.`
        );
      }
    }

    // Grupos de exclusão em que ESTA cobertura participa: o dela própria e o do
    // benefício que ela tem escolhido. Para cada um, acusa os demais membros
    // ativos do mesmo grupo que pertençam a OUTRA cobertura. Assim, num
    // conflito benefício↔cobertura, tanto a linha da cobertura quanto a da
    // cobertura-pai do benefício mostram a mensagem.
    if (enabled) {
      for (const ownGroup of exclusionGroupsOwnedBy(cov, selections[code])) {
        const clashing = activeExclusionMembers.filter(
          member => member.groupCode === ownGroup && member.ownerCode !== code
        );
        // Conflito com alternativa (ex.: Auxílio × Assistência Funeral) tem
        // mensagem própria por par; os demais seguem na mensagem genérica.
        const withoutAlternative: ActiveExclusionMember[] = [];
        for (const member of clashing) {
          const message = buildExclusionWithAlternativeMessage(
            cov,
            byCode.get(member.ownerCode),
            schema.coverages
          );
          if (!message) withoutAlternative.push(member);
          else if (!errors.includes(message)) errors.push(message);
        }
        if (withoutAlternative.length > 0) {
          errors.push(
            `É proibida a contratação da cobertura ${cov.name} junto com ${withoutAlternative
              .map(member => member.name)
              .join(', ')}.`
          );
        }
      }
    }

    // Acoplamento de nível (`LinkedBenefitCode`): pré-computado, aparece nas
    // linhas das duas coberturas envolvidas.
    errors.push(...(benefitLinkConflicts.get(code) ?? []));

    const r: ResolvedCoverage = {
      code,
      enabled,
      resolvedCapital,
      errors,
    };
    out[code] = r;
    return r;
  };

  for (const cov of schema.coverages) {
    resolve(cov.code, new Set());
  }

  return out;
}

/** Um item selecionado que participa de algum grupo de exclusão. */
type ActiveExclusionMember = {
  /** Nome exibido na mensagem de conflito. */
  name: string;
  /** Valor do `ExclusionGroupCode`. */
  groupCode: string;
  /** Cobertura em cuja linha o item aparece (a própria, ou a pai do benefício). */
  ownerCode: string;
};

/**
 * O benefício ESCOLHIDO de uma cobertura de seleção, quando há um. Só os dois
 * modos de seleção carregam `selectedChildCoverageCode` + `children`.
 */
function selectedBenefitChild(
  cov: CoverageDefinition,
  selection: CoverageSelection | undefined
): BenefitSelectionChild | null {
  if (
    (selection?.kind === 'BENEFIT_SELECTION' ||
      selection?.kind === 'BENEFIT_SELECTION_WITH_SI') &&
    selection.selectedChildCoverageCode &&
    'children' in cov
  ) {
    return (
      cov.children.find(
        child => child.code === selection.selectedChildCoverageCode
      ) ?? null
    );
  }
  return null;
}

/**
 * Mensagem de conflito de exclusão COM ALTERNATIVA. Quando exatamente uma
 * cobertura do produto depende (`RelationType=1`) de uma das duas em conflito,
 * o corretor é orientado a contratá-la no lugar. No 930016 é a Assistência
 * Funeral Simples, que depende do Auxílio Funeral equivalente — então o par
 * Auxílio × Assistência Funeral ganha a sugestão e o Auxílio vem em segundo.
 *
 * Tudo sai dos dados do catálogo (nenhum nome ou código de produto aqui).
 * Sem alternativa única (ex.: Auxílio × Auxílio) devolve `null` e vale a
 * mensagem genérica.
 */
function buildExclusionWithAlternativeMessage(
  current: CoverageDefinition,
  other: CoverageDefinition | undefined,
  coverages: CoverageDefinition[]
): string | null {
  if (!other) return null;

  const alternatives = coverages.filter(
    candidate =>
      candidate.relationType === '1' &&
      (candidate.parentCoverageCode === current.code ||
        candidate.parentCoverageCode === other.code)
  );
  if (alternatives.length !== 1) return null;

  const [alternative] = alternatives;
  const parent =
    alternative.parentCoverageCode === current.code ? current : other;
  const sibling = parent === current ? other : current;

  return `${sibling.name} e ${parent.name} não podem ser contratados na mesma cotação. Selecione apenas uma das opções ou contrate a ${alternative.name}.`;
}

/** Grupos de exclusão que uma cobertura habilitada "ocupa": o dela e o do
 *  benefício escolhido. Vazio quando nenhum tem `exclusionGroupCode`. */
function exclusionGroupsOwnedBy(
  cov: CoverageDefinition,
  selection: CoverageSelection | undefined
): string[] {
  const groups: string[] = [];
  if (cov.exclusionGroupCode) groups.push(cov.exclusionGroupCode);
  const child = selectedBenefitChild(cov, selection);
  if (child?.exclusionGroupCode) groups.push(child.exclusionGroupCode);
  return groups;
}

/**
 * Enumera todos os membros ativos de grupos de exclusão no estado atual:
 * coberturas habilitadas com `exclusionGroupCode` e o benefício escolhido de
 * cada cobertura de seleção habilitada. Não depende da ordem de resolução —
 * por isso é calculado uma vez antes do loop.
 */
function collectActiveExclusionMembers(
  coverages: CoverageDefinition[],
  selections: Record<string, CoverageSelection | undefined>
): ActiveExclusionMember[] {
  const members: ActiveExclusionMember[] = [];
  for (const cov of coverages) {
    const selection = selections[cov.code];
    if (!selection?.enabled) continue;

    if (cov.exclusionGroupCode) {
      members.push({
        name: cov.name,
        groupCode: cov.exclusionGroupCode,
        ownerCode: cov.code,
      });
    }

    const child = selectedBenefitChild(cov, selection);
    if (child?.exclusionGroupCode) {
      members.push({
        name: child.name,
        groupCode: child.exclusionGroupCode,
        ownerCode: cov.code,
      });
    }
  }
  return members;
}

/** Código do benefício ESCOLHIDO numa cobertura de seleção (ou null). */
function selectedBenefitCode(
  selection: CoverageSelection | undefined
): string | null {
  if (
    selection?.kind === 'BENEFIT_SELECTION' ||
    selection?.kind === 'BENEFIT_SELECTION_WITH_SI'
  ) {
    return selection.selectedChildCoverageCode ?? null;
  }
  return null;
}

/**
 * Acoplamento de NÍVEL entre coberturas pareadas (`LinkedBenefitCode`).
 *
 * O benefício escolhido numa cobertura A (ex.: Assistência Funeral Individual)
 * carrega o `linkedBenefitCode` = o CÓDIGO do benefício equivalente na
 * cobertura pareada P. A pareada é resolvida por `A.parentCoverageCode` — NÃO
 * por "quem contém o código", porque os códigos do Auxílio são compartilhados
 * entre os Auxílios Individual/Familiar/Ampliado e sozinhos são ambíguos.
 *
 * Só dispara com AS DUAS contratadas: se A e P estão habilitadas e P tem um
 * benefício escolhido diferente do `linkedBenefitCode` → conflito. O erro
 * aparece nas DUAS linhas (A e P) para o corretor entender o que igualar. Com
 * uma só contratada, ou P ainda sem escolha, não restringe.
 */
function collectBenefitLinkConflicts(
  coverages: CoverageDefinition[],
  selections: Record<string, CoverageSelection | undefined>,
  byCode: Map<string, CoverageDefinition>
): Map<string, string[]> {
  const conflicts = new Map<string, string[]>();
  const push = (code: string, message: string) => {
    const list = conflicts.get(code);
    if (list) list.push(message);
    else conflicts.set(code, [message]);
  };

  for (const cov of coverages) {
    const selection = selections[cov.code];
    if (!selection?.enabled) continue;

    const child = selectedBenefitChild(cov, selection);
    const required = child?.linkedBenefitCode;
    if (!required || !cov.parentCoverageCode) continue;

    const paired = byCode.get(cov.parentCoverageCode);
    if (!paired || !selections[paired.code]?.enabled) continue;

    // P ainda sem benefício escolhido → não acusa (a exigência de "escolha um
    // valor" é tratada em outro ponto); só há mismatch quando P já tem escolha.
    const pairedSelected = selectedBenefitCode(selections[paired.code]);
    if (!pairedSelected || pairedSelected === required) continue;

    // `child.name` é o valor do nível na cobertura A (ex.: "R$ 5.000"), que é
    // o valor exigido em ambos os lados por serem o mesmo nível.
    const message = `As coberturas ${cov.name} e ${paired.name} devem ter o mesmo valor (${child.name}).`;
    push(cov.code, message);
    push(paired.code, message);
  }

  return conflicts;
}

function readRiskBasis(code: string, ctx: ResolverContext): number {
  // Códigos conhecidos. Quando aparecer um código novo do produto técnico,
  // adicionar aqui ou via `ctx.riskBases` injetado pelo chamador.
  // Default: capital total das vidas (não bloqueia a UI por código novo).
  switch (code) {
    case 'MemberCapital':
    case 'Capital':
    case 'Salary':
      // Default seguro enquanto produto não confirma a agregação correta.
      return ctx.totalMemberCapital;
    default: {
      const injected = ctx.riskBases?.[code];
      if (injected != null) return injected;
      // Código desconhecido: usa capital total das vidas como fallback.
      // eslint-disable-next-line no-console
      console.warn(
        `[group-life] RiskBasisCode "${code}" não mapeado — usando capital total das vidas como base.`
      );
      return ctx.totalMemberCapital;
    }
  }
}
